<template>
    <date-range-selectable
        :modelValue="dateRange"
        @update:modelValue="handleDateRange"
        :options="options"
        :formatRange="formatRange"
        :minDate="minDate"
        :maxDate="maxDate"
        :maxRangeDays="MAX_DAYS_RANGE"
        :timezone="timezone"
    />
</template>

<script setup>
import { computed } from 'vue';
import { controlDateRangeOptions, formatDateForRange, getCurrentDate, getMinAllowedDate } from '~/utils/analytics';
import { getClientTz, shortDate } from '~/utils/date';
import dayjs from '~/utils/dayjs';
import DateRangeSelectable from '~/components/general/DateRangeSelectable.vue';

// dayjs calculates an end date starting from a next day. in order to get the right range including a current day we decrease max days by 1.
const MAX_DAYS_RANGE = 99;

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
});

const timezone = computed(() => getClientTz());

const options = computed(() => controlDateRangeOptions(Boolean(props.groupId), Boolean(props.orgUnitId)));

const minDate = computed(() => getMinAllowedDate(Boolean(props.groupId), Boolean(props.orgUnitId)).toDate());

const maxDate = computed(() => {
    const currentDate = getCurrentDate();

    // current day is only available if the time is at least 01:30 local.
    if (currentDate.isAfter(currentDate.clone().set('hour', 1).set('minutes', 30))) {
        return currentDate.toDate();
    }

    return currentDate.subtract(1, 'day').endOf('day').toDate();
});

const dateRange = computed(() => ({
    start_date: props.modelValue.start_date,
    end_date: props.modelValue.end_date,
}));

const formatRange = (start, end) => ({
    start_date: formatDateForRange(start),
    end_date: formatDateForRange(dayjs.min([getCurrentDate(), end.add(1, 'day')])),
});

const handleDateRange = (range) => {
    const preset = options.value.find(
        (option) => option.start_date === range.start_date && option.end_date === range.end_date
    );

    if (preset) {
        emit('update:modelValue', { ...preset });
        return;
    }

    emit('update:modelValue', {
        value: 'selected_custom',
        label: `${shortDate(range.start_date)} - ${shortDate(range.end_date)}`,
        start_date: range.start_date,
        end_date: range.end_date,
    });
};
</script>
